import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Box, Button, Typography } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import {
  BROWSE_MARKETPLACE_LABEL,
  EXPLORE_BODY,
  EXPLORE_CARD_ICON_SIZE,
  EXPLORE_TITLE,
  getKitExploreBodyText,
  KIT_EXPLORE_TITLE,
  MARKETPLACE_TOTAL_CREDITS,
} from '../../../constants/marketplace';
import { CARD_MAX_WIDTH, HOVER_MEDIA, LOCKED_OPACITY } from '../../../constants/ui';
import { isActivationKey } from '../../../util/ui';

interface IProps {
  onBrowse: () => void;
  isKitAlternative?: boolean;
  isDisabled?: boolean;
}

// Mirrors StyledCard's box model so the placeholder sizes like a campaign card in grid and carousel.
const cardSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: CARD_MAX_WIDTH,
  height: '100%',
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  borderRadius: 1,
  // divider is too faint for a dashed outline to read as a card edge.
  border: ({ palette }) => `1px dashed ${palette.action.disabled}`,
  backgroundColor: 'background.paper',
  cursor: 'pointer',
  transition: ({ transitions }) => transitions.create(['background-color'], { duration: transitions.duration.shorter }),
  [HOVER_MEDIA]: {
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
};

// Mirrors the square media block that sets a campaign card's height; `flex: 1 0 auto` because
// shrinking would let the block collapse under the aspect ratio.
const bodySx: SxProps<Theme> = {
  flex: '1 0 auto',
  width: '100%',
  aspectRatio: '1 / 1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const iconBadgeSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: EXPLORE_CARD_ICON_SIZE,
  height: EXPLORE_CARD_ICON_SIZE,
  mb: 1,
  borderRadius: '50%',
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity),
};

export const MarketplaceExploreCard: React.FC<IProps> = ({
  onBrowse,
  isKitAlternative = false,
  isDisabled = false,
}) => {
  const handleBrowse = useCallback(() => {
    if (!isDisabled) {
      onBrowse();
    }
  }, [onBrowse, isDisabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (isActivationKey(e)) {
        e.preventDefault();
        handleBrowse();
      }
    },
    [handleBrowse],
  );

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleBrowse();
    },
    [handleBrowse],
  );

  const cardStateSx = useMemo<SxProps<Theme>>(
    () => (isDisabled ? { ...cardSx, cursor: 'default', opacity: LOCKED_OPACITY, pointerEvents: 'none' } : cardSx),
    [isDisabled],
  );

  const copy = useMemo(
    () =>
      isKitAlternative
        ? { title: KIT_EXPLORE_TITLE, body: getKitExploreBodyText(MARKETPLACE_TOTAL_CREDITS) }
        : { title: EXPLORE_TITLE, body: EXPLORE_BODY },
    [isKitAlternative],
  );

  return (
    <Box
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={copy.title}
      aria-disabled={isDisabled}
      onClick={handleBrowse}
      onKeyDown={handleKeyDown}
      sx={cardStateSx}
    >
      <Box sx={bodySx}>
        <Box sx={iconBadgeSx}>
          <StorefrontOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight="regular" gutterBottom>
          {copy.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {copy.body}
        </Typography>
      </Box>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        size="medium"
        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
        disabled={isDisabled}
        onClick={handleButtonClick}
      >
        {BROWSE_MARKETPLACE_LABEL}
      </Button>
    </Box>
  );
};
